import HudView from '../../View/HudView';
import { Popup, Popups } from '../Popups';
import { IStateMissions } from '../../State/reducers/missions';
import { checkForNotification } from '../../State/reducers/social';
import { Actions } from '../../State/Actions/Actions';
import Core from '../../Core/Core';
import { Manager } from '../Manager';
import ResourceTracking from '../Tracking/ResourceTracking';
import {
    TGameAreas,
    TransactionActionTypes,
    TransactionTypes,
} from '../../DeltaTracking/ResourceParamBuilder';
import { AdContexts, AdGameAreas, AdRewardTypes } from '../../DeltaTracking/AdParamBuilder';
import { HudActions, HudEmitter } from '../Hud/HudEmitter';
import { IGameState } from '../../State/Store/store';
import { INews } from '../../UI/PopUps/Social/News';
import { COOLDOWN } from '../../UI/PopUps/Social/GiftFriendTab';
import { Collection } from '../../Tiles/TileSet';
import SocialTracking from '../Tracking/SocialTracking';
import { sizer } from '../../index';
import { IStateSocial } from '../../State/socialHelpers';

export class SocialHub extends Manager {
    private view: HudView;
    private manager: Popups;
    private closeAction: (layer: number, n: number, args: any[]) => void;

    constructor(view: HudView, parent: Popups) {
        super();

        this.view = view;
        this.manager = parent;

        this.addSelector(
            (state: IStateSocial, pre: IStateSocial) => {
                this.handleSharedGiftsChange(state, pre);
            },
            (state: IGameState) => state.StateSocial
        );

        this.addSelector(
            (state: IStateMissions, pre: IStateMissions) => {
                this.handleMissionChange(state, pre);
            },
            (state: IGameState) => state.StateMissions
        );

        HudEmitter.instance.on(
            HudActions.MISSION_COLLECT_COINS,
            this.handleMissionCoins.bind(this)
        );
        HudEmitter.instance.on(
            HudActions.MISSION_SELECT_REFRESH,
            this.handleMissionRefresh.bind(this)
        );
        HudEmitter.instance.on(
            HudActions.MISSION_MILESTONE_COLLECTED,
            this.handleMissionMilestone.bind(this)
        );
        HudEmitter.instance.on(HudActions.SET_COLLECTION, this.handleSetCollection.bind(this));

        this.closeAction = (_, n: number, args: any[]) => {
            if (n === Popup.SOCIAL) {
                this.handlePopupClosed(args[0]);
            }
        };
    }

    public async addSocialHub() {
        HudEmitter.instance.emit(HudActions.SOCIALHUB_START_LOAD_SOCIAL);
        const weekly = await Core.sdk.leaderboards.getLeaderBoardData(
            'weeklyLeaderboards',
            Core.sdk.leaderboards.getCurrentWeekID()
        );
        const leaderboard = await Core.sdk.leaderboards.getLeaderBoardData(
            'leaderboard',
            'default'
        );
        const allNews = Core.gameCache.jsons.news;
        const news = allNews[Core.local.languageKey] as INews[];

        HudEmitter.instance.emit(HudActions.SOCIALHUB_STOP_LOAD_SOCIAL);
        await this.view.createSocialPopup(
            Core.store.getState().StateSocial.pendingInvitations,
            Core.store.getState().StateSocial.sharedGiftsCount,
            weekly,
            leaderboard,
            news
        );

        if (
            checkForNotification(Core.store.getState().StateSocial.sentGifts, COOLDOWN) ||
            checkForNotification(Core.store.getState().StateSocial.requestedGifts, COOLDOWN)
        ) {
            this.view.socialPopup.showGiftTabNotificationIcon();
        }

        HudEmitter.instance.on(HudActions.POPUP_CLOSED, this.closeAction);
    }

    /**
     * updates the view journal popup if a mission changes while it is shown
     */
    private handleMissionChange(state: IStateMissions, previous: IStateMissions) {
        let change = false;
        const missions = state.missions.filter(Boolean);
        missions.forEach((m, index) => {
            if (m === null && previous.missions[index] !== null) {
                change = true;
                return;
            }

            if ((m && !previous.missions[index]) || (m && m.uid !== previous.missions[index].uid)) {
                change = true;
                return;
            }

            if (m && m.missionState !== previous.missions[index].missionState) {
                change = true;
                return;
            }
        });

        if (change && this.view.journalPopup) {
            this.view.journalPopup.update(state);
        }
    }

    /**
     * updates the gift reward counter
     * if it hits 10, resets it and gives the player a reward
     * @param state the current state stream that was listened to
     * @param previous the previous state stream
     */
    private handleSharedGiftsChange(state: IStateSocial, previous: IStateSocial) {
        if (state.sharedGiftsCount !== previous.sharedGiftsCount) {
            if (state.sharedGiftsCount >= 10) {
                this.view.socialPopup.giveReward().catch(err => {
                    console.log(['error with social popup give reward', err]);
                });
                Core.store.dispatch(Actions.resetGiftCounter());
                this.view.socialPopup.getGiftTab().updateProgressBar(0);
            } else {
                this.view.socialPopup.getGiftTab().updateProgressBar(state.sharedGiftsCount);
            }
        }
    }

    // missions
    public handleMissionCoins(args: [number, string]) {
        Core.store.dispatch(Actions.updateMissionCount());
        Core.store.dispatch(Actions.updateCoins(args[0]));
        Core.store.dispatch(Actions.missionRefresh(args[1]));

        ResourceTracking.sendCoinsResourceEvent(
            args[0],
            TransactionTypes.REWARD,
            TransactionActionTypes.DAILY_MISSION,
            TGameAreas.MAP
        );
    }

    // missions
    public async handleMissionRefresh(id: string) {
        this.manager.adSuccessAction = () => {
            Core.store.dispatch(Actions.missionReplace(id));
        };
        this.manager.adErrorAction = () => {};
        Core.delta.setAdParams(AdRewardTypes.NONE, AdContexts.MISSION_SKIP, AdGameAreas.MAP, -1);

        await this.manager.watchRewardedAd('skipMission');
    }

    public handleMissionMilestone(milestone: number) {
        Core.store.dispatch(Actions.missionMilestone(milestone));
    }

    public handleSetCollection(collection: Collection) {
        Core.store.dispatch(Actions.seActiveCollection(collection));
    }

    private async handlePopupClosed(action: string) {
        if (action === 'close') {
            HudEmitter.instance.off(HudActions.POPUP_CLOSED, this.closeAction);
            await this.view.deleteSocialPopup();
        }
        if (action === 'invite') {
            SocialTracking.clickInvite();
            this.view.createLoadSocial();
            const returned = await Core.sdk.messages.sendInvite();
            if (returned) {
                this.view.socialPopup.leaderBoard.updateLeaderboard();
            }
            await this.view.removeLoadSocial();
            sizer();
        }
    }
}
